import React, { useState, useMemo } from 'react';
import {
    Box,
    Button,
    Collapse,
    MenuItem,
    Select,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faCalendarDay, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { getWorkItemTypeLabel, linkedEmailMessageId } from '../utils/workItemDisplay';
import {
    DrawerFacetWorkProps,
    toDateInputValue,
    fromDateInputValue,
    toTimeInputValue,
    fromDateTimeInputValue,
    scheduleBadgeLabel,
} from './DrawerFacetTypes';
import { effectiveUserTimeZone } from '../utils/userTime';

const sectionCard = {
    borderRadius: 2,
    border: '1px solid var(--color-border)',
    bgcolor: 'var(--color-bg-tertiary)',
    overflow: 'hidden',
};

const sectionHeader = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    px: 2,
    py: 1.25,
    cursor: 'pointer',
    userSelect: 'none' as const,
    border: 'none',
    bgcolor: 'transparent',
    textAlign: 'left' as const,
    font: 'inherit',
    '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb, 99, 102, 241), 0.04)' },
    '&:focus-visible': {
        outline: '2px solid var(--color-accent)',
        outlineOffset: '-2px',
    },
};

interface SectionProps {
    title: string;
    defaultOpen: boolean;
    badge?: string | null;
    children: React.ReactNode;
}

// Disclosure pattern: the whole header is a real <button> with aria-expanded /
// aria-controls so keyboard and assistive-tech users can toggle sections (WCAG 2.1.1).
const Section: React.FC<SectionProps> = ({ title, defaultOpen, badge, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const contentId = `wb-drawer-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
        <Box sx={sectionCard}>
            <Box
                component="button"
                type="button"
                aria-expanded={open}
                aria-controls={contentId}
                data-testid={`WB_DRAWER_SECTION_TOGGLE_${title.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}`}
                sx={sectionHeader}
                onClick={() => setOpen((v) => !v)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {title}
                    </Typography>
                    {badge && (
                        <Typography sx={{ fontSize: '0.68rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                            {badge}
                        </Typography>
                    )}
                </Box>
                <Box component="span" aria-hidden sx={{ color: 'var(--color-text-secondary)', display: 'inline-flex', p: 0.25 }}>
                    <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} style={{ fontSize: '0.7rem' }} />
                </Box>
            </Box>
            <Collapse in={open} timeout={reducedMotion ? 0 : 180}>
                <Box id={contentId} sx={{ px: 2, pb: 2, display: 'grid', gap: 2.5 }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};

const DrawerFacetWork: React.FC<DrawerFacetWorkProps> = ({
    item,
    draft,
    onFieldChange,
    isTriageState,
    isJobDomain,
    timeZone,
    domainMenu,
    domainFieldEntries,
    currentStateLabel,
    showEventDate,
    setShowEventDate,
    suggestedActionDate,
    prepDaysUsed,
    detectedEventType,
    applySuggestion,
    startOnEventDay,
}) => {
    const scheduleTimeZone = effectiveUserTimeZone(timeZone);
    const showOriginalEmail = Boolean(linkedEmailMessageId(item));
    const classificationPopulated = useMemo(() => {
        const hasType = !!draft.type && draft.type !== 'INBOX';
        const hasDomain = !!draft.domain && draft.domain !== 'general';
        const hasTags = Array.isArray(draft.tags) && draft.tags.length > 0;
        return hasType && hasDomain && !hasTags ? false : hasType || hasDomain || hasTags;
    }, [draft.type, draft.domain, draft.tags]);

    const schedulingNeedsAttention = useMemo(() => {
        if (!draft.due_date) return true;
        const d = new Date(draft.due_date as string);
        return !Number.isNaN(d.getTime()) && d < new Date();
    }, [draft.due_date]);

    return (
        <Box sx={{ display: 'grid', gap: 2 }}>
            {/* ── Core info ── always expanded. Title first: it is the item's identity. */}
            <Section title="Core info" defaultOpen>
                <TextField
                    label="Title"
                    value={draft.title ?? ''}
                    onChange={(e) => onFieldChange('title', e.target.value)}
                    fullWidth
                    variant="filled"
                    InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                    InputLabelProps={{ sx: { color: 'var(--color-text-secondary)' } }}
                    sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                />

                <TextField
                    label="Notes"
                    value={draft.notes ?? ''}
                    onChange={(e) => onFieldChange('notes', e.target.value)}
                    placeholder="Capture follow-up ideas, draft replies, or reminders."
                    fullWidth
                    multiline
                    minRows={3}
                    variant="filled"
                    InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                    InputLabelProps={{ sx: { color: 'var(--color-text-secondary)' } }}
                    sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                    helperText={showOriginalEmail ? 'Your notes — not the original email.' : undefined}
                    FormHelperTextProps={{ sx: { color: 'var(--color-text-muted)', fontSize: '0.75rem' } }}
                />

                <TextField
                    label="Summary / snippet"
                    value={draft.summary ?? ''}
                    onChange={(e) => onFieldChange('summary', e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    variant="filled"
                    InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                    InputLabelProps={{ sx: { color: 'var(--color-text-secondary)' } }}
                    sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                    helperText={
                        showOriginalEmail
                            ? 'Read the full message in Overview.'
                            : undefined
                    }
                    FormHelperTextProps={{ sx: { color: 'var(--color-text-muted)', fontSize: '0.75rem' } }}
                />
            </Section>

            {/* ── Classification ── collapsed when already populated */}
            <Section
                title="Classification"
                defaultOpen={!classificationPopulated}
                badge={classificationPopulated ? `${domainMenu.find((d) => d.key === (draft.domain || 'general'))?.label || draft.domain || 'general'} · ${getWorkItemTypeLabel(draft.type) || 'Inbox'}` : null}
            >
                {domainFieldEntries.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                            Domain details
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            {domainFieldEntries.map((field) => (
                                <Box key={field.key} sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                                        {field.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', wordBreak: 'break-word' }}>
                                        {field.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {isJobDomain && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            label="Company"
                            value={draft.company ?? ''}
                            onChange={(e) => onFieldChange('company', e.target.value)}
                            fullWidth size="small" variant="filled"
                            InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                            InputLabelProps={{ sx: { color: 'var(--color-text-secondary)' } }}
                            sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                        />
                        <TextField
                            label="Status"
                            value={draft.status ?? ''}
                            onChange={(e) => onFieldChange('status', e.target.value)}
                            fullWidth size="small" variant="filled"
                            placeholder="Applied, Interview..."
                            InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                            InputLabelProps={{ sx: { color: 'var(--color-text-secondary)' } }}
                            sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                        />
                        <TextField
                            label="Job title"
                            value={draft.job_title ?? ''}
                            onChange={(e) => onFieldChange('job_title', e.target.value)}
                            fullWidth size="small" variant="filled"
                            InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                            InputLabelProps={{ sx: { color: 'var(--color-text-secondary)' } }}
                            sx={{ gridColumn: '1 / -1', '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                        />
                    </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    {!isTriageState && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', mb: 0.5, display: 'block' }}>
                                Type
                            </Typography>
                            <Select
                                fullWidth
                                value={draft.type || 'INBOX'}
                                onChange={(e) => onFieldChange('type', e.target.value)}
                                variant="filled"
                                sx={{ color: 'var(--color-text-primary)', bgcolor: 'var(--color-bg-secondary)', '& .MuiSvgIcon-root': { color: 'var(--color-text-primary)' } }}
                            >
                                <MenuItem value="INBOX">{getWorkItemTypeLabel('INBOX')}</MenuItem>
                                <MenuItem value="APPLICATION">{getWorkItemTypeLabel('APPLICATION')}</MenuItem>
                                <MenuItem value="TASK">{getWorkItemTypeLabel('TASK')}</MenuItem>
                                {draft.type === 'NOTE' && <MenuItem value="NOTE">{getWorkItemTypeLabel('NOTE')}</MenuItem>}
                                {draft.type === 'LEARNING' && <MenuItem value="LEARNING">{getWorkItemTypeLabel('LEARNING')}</MenuItem>}
                            </Select>
                        </Box>
                    )}
                    <Box>
                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mb: 0.5, display: 'block' }}>
                            State
                        </Typography>
                        <Typography sx={{
                            color: 'var(--color-text-secondary)',
                            p: 1,
                            border: '1px dashed var(--color-border)',
                            borderRadius: 1,
                            bgcolor: 'transparent',
                            fontStyle: 'italic',
                            fontSize: '0.875rem',
                        }}>
                            {currentStateLabel}
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', mb: 0.5, display: 'block' }}>
                        Domain
                    </Typography>
                    <Select
                        fullWidth
                        data-testid="WB_DRAWER_DOMAIN_SELECT"
                        value={draft.domain || 'general'}
                        onChange={(e) => onFieldChange('domain', e.target.value)}
                        variant="filled"
                        sx={{ color: 'var(--color-text-primary)', bgcolor: 'var(--color-bg-secondary)', '& .MuiSvgIcon-root': { color: 'var(--color-text-primary)' } }}
                    >
                        {domainMenu.map((d) => (
                            <MenuItem key={d.key} value={d.key} disabled={d.disabled}>
                                {d.icon} {d.label}{d.statusLabel ? ` (${d.statusLabel})` : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <TextField
                    label="Tags (comma-separated)"
                    value={(draft.tags || []).join(', ')}
                    onChange={(e) =>
                        onFieldChange('tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))
                    }
                    fullWidth
                    variant="filled"
                    InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                    InputLabelProps={{ sx: { color: 'var(--color-text-secondary)' } }}
                    sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                />
            </Section>

            {/* ── Scheduling ── expanded when dates missing or overdue */}
            <Section
                title="Scheduling"
                defaultOpen={schedulingNeedsAttention}
                badge={!schedulingNeedsAttention ? scheduleBadgeLabel(draft.due_date as any) : null}
            >
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                        label="Next action date"
                        type="date"
                        data-testid="WB_DRAWER_SCHEDULE_DATE"
                        value={toDateInputValue(draft.due_date as any)}
                        onChange={(e) =>
                            onFieldChange(
                                'due_date',
                                fromDateTimeInputValue(
                                    e.target.value,
                                    toTimeInputValue(draft.due_date as any),
                                    { timeZone: scheduleTimeZone },
                                ),
                            )
                        }
                        InputLabelProps={{ shrink: true, sx: { color: 'var(--color-text-secondary)' } }}
                        fullWidth
                        variant="filled"
                        InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                        sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                    />
                    <TextField
                        label="Time"
                        type="time"
                        data-testid="WB_DRAWER_SCHEDULE_TIME"
                        value={toTimeInputValue(draft.due_date as any)}
                        onChange={(e) =>
                            onFieldChange(
                                'due_date',
                                fromDateTimeInputValue(
                                    toDateInputValue(draft.due_date as any),
                                    e.target.value,
                                    { fillMissingDate: true, timeZone: scheduleTimeZone },
                                ),
                            )
                        }
                        InputLabelProps={{ shrink: true, sx: { color: 'var(--color-text-secondary)' } }}
                        fullWidth
                        variant="filled"
                        InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                        inputProps={{ step: 60 }}
                        sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                    />
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontSize: '0.75rem', mt: -1 }}>
                    Controls where the item appears on Today
                </Typography>

                {!showEventDate ? (
                    <Button
                        onClick={() => setShowEventDate(true)}
                        sx={{
                            color: 'var(--color-accent-text)',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            justifyContent: 'flex-start',
                            padding: '4px 8px',
                            '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.1)' },
                        }}
                    >
                        + Add event date (optional)
                    </Button>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: 'rgba(var(--color-accent-rgb), 0.05)', borderRadius: 2, border: '1px solid rgba(var(--color-accent-rgb), 0.2)' }}>
                        <Typography variant="caption" sx={{ color: 'var(--color-accent-text)', fontWeight: 600 }}>
                            Event & prep time
                        </Typography>

                        <TextField
                            label="Event date"
                            type="date"
                            value={toDateInputValue(draft.event_date as any)}
                            onChange={(e) => onFieldChange('event_date', fromDateInputValue(e.target.value))}
                            InputLabelProps={{ shrink: true, sx: { color: 'var(--color-text-secondary)' } }}
                            fullWidth
                            variant="filled"
                            InputProps={{ sx: { color: 'var(--color-text-primary)' } }}
                            sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                            helperText="When does the event actually happen?"
                            FormHelperTextProps={{ sx: { color: 'var(--color-text-muted)', fontSize: '0.75rem' } }}
                        />

                        <Select
                            value={draft.event_type || ''}
                            onChange={(e) => onFieldChange('event_type', e.target.value || null)}
                            displayEmpty
                            fullWidth
                            variant="filled"
                            sx={{ color: 'var(--color-text-primary)', '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                        >
                            <MenuItem value=""><em>Auto-detect event type</em></MenuItem>
                            <MenuItem value="interview">Interview</MenuItem>
                            <MenuItem value="meeting">Meeting</MenuItem>
                            <MenuItem value="presentation">Presentation</MenuItem>
                            <MenuItem value="exam">Exam/Test</MenuItem>
                            <MenuItem value="flight">Flight/Travel</MenuItem>
                            <MenuItem value="appointment">Appointment</MenuItem>
                            <MenuItem value="deadline">Deadline</MenuItem>
                        </Select>

                        <TextField
                            label="Prep days"
                            type="number"
                            value={draft.prep_days_override ?? ''}
                            onChange={(e) => onFieldChange('prep_days_override', e.target.value ? Number(e.target.value) : null)}
                            InputLabelProps={{ shrink: true, sx: { color: 'var(--color-text-secondary)' } }}
                            fullWidth
                            variant="filled"
                            InputProps={{
                                sx: { color: 'var(--color-text-primary)' },
                                inputProps: { min: 0, max: 30 },
                            }}
                            sx={{ '& .MuiFilledInput-root': { bgcolor: 'var(--color-bg-secondary)' } }}
                            helperText="Override default prep time for this item"
                            FormHelperTextProps={{ sx: { color: 'var(--color-text-muted)', fontSize: '0.75rem' } }}
                        />

                        {suggestedActionDate && (
                            <Box sx={{ mt: 1, p: 2, bgcolor: 'rgba(var(--color-accent-rgb), 0.1)', borderRadius: 1, border: '1px solid rgba(var(--color-accent-rgb), 0.3)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <FontAwesomeIcon icon={faLightbulb} style={{ color: 'var(--color-accent)' }} />
                                    <Typography variant="body2" sx={{ color: 'var(--color-accent-text)', fontWeight: 600 }}>
                                        Suggestion
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', mb: 2 }}>
                                    {detectedEventType && (
                                        <span style={{ color: 'var(--color-accent-text)' }}>
                                            {detectedEventType.charAt(0).toUpperCase() + detectedEventType.slice(1)}
                                        </span>
                                    )}
                                    {detectedEventType && ': '}
                                    Start {prepDaysUsed} day{prepDaysUsed !== 1 ? 's' : ''} before on{' '}
                                    <strong>{new Date(suggestedActionDate).toLocaleDateString()}</strong>
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={applySuggestion}
                                        startIcon={<FontAwesomeIcon icon={faLightbulb} />}
                                        sx={{
                                            bgcolor: 'var(--color-accent)',
                                            color: 'var(--color-on-accent)',
                                            '&:hover': { bgcolor: 'var(--color-accent-hover)', color: 'var(--color-on-accent-hover)' },
                                        }}
                                    >
                                        Apply suggestion
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={startOnEventDay}
                                        startIcon={<FontAwesomeIcon icon={faCalendarDay} />}
                                        sx={{
                                            color: 'var(--color-text-primary)',
                                            borderColor: 'rgba(var(--color-accent-rgb), 0.5)',
                                            '&:hover': {
                                                borderColor: 'var(--color-accent)',
                                                bgcolor: 'rgba(var(--color-accent-rgb), 0.1)',
                                            },
                                        }}
                                    >
                                        Start on event day
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Section>
        </Box>
    );
};

export default DrawerFacetWork;
